import React, { useEffect } from 'react';
import { Box, Typography, LinearProgress, useMediaQuery } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../redux/storeExports';
import { theme } from '../../../theme/DashboardTheme';
import scanningAnimation from 'shared/assets/lottie/scan.gif';
import shield from 'shared/assets/icons/business_userscan.svg';
import shieldTick from 'shared/assets/icons/shield-colored.svg';

import { ProfileSelectors, FetchSecondaryScanStatus } from "shared/redux/profile/ProfileSlice";
import { selectMuid } from "shared/redux/authentication/authenticationSlice";

const SCAN_ACTIVE_THRESHOLD_HOURS = 3;
export const ScanWidget = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const profile = useAppSelector(ProfileSelectors.selectProfile);

  const remainingScans = useAppSelector(ProfileSelectors.selectSecondaryScanRemaining);
  const totalScans = useAppSelector(ProfileSelectors.selectSecondaryScanTotal);
  const muid = useAppSelector(selectMuid);

  const lastScanStart = profile?.last_scan_initiated_at;
  // const scanCompletionMailSentAt = profile?.scan_completed_mail_sent_at;
  const lastScanCompletedAt = profile?.last_scan_completed_at;
  const lastScanCompletedAtDate = lastScanCompletedAt ? new Date(lastScanCompletedAt) : null;

  const lastScanStartDate = lastScanStart ? new Date(lastScanStart) : null;
  const currentTime = new Date();

  const hoursDifference = lastScanStartDate
    ? (currentTime.getTime() - lastScanStartDate.getTime()) / (1000 * 60 * 60)
    : Infinity;

  const isScanActive =
    !!lastScanStartDate && hoursDifference < SCAN_ACTIVE_THRESHOLD_HOURS;

  const completedScans = totalScans - remainingScans;
  const progress = totalScans > 0 ? (completedScans / totalScans) * 100 : 0;

  useEffect(() => {
    if (isScanActive && muid) {
      dispatch(FetchSecondaryScanStatus(muid));
    }
  }, [isScanActive, muid, dispatch]);

  if (!lastScanStart || !lastScanStartDate) {
    return null;
  }

  const scanDone = () => {
    return (
      <Box
        p={isMobile ? 1 : 2}
        sx={{
          display: 'flex',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: isMobile ? 2 : 0,
            py: isMobile ? 1 : 0,
            borderRadius: isMobile ? 2 : 0,
          }}
        >
          <img
            src={shieldTick}
            alt="Shield Tick"
            style={{
              width: '16px',
              height: '20px',
              display: 'block',
              margin: 'auto',
              marginLeft: '10px',
              marginRight: '10px',
            }}
          />
          <Typography
            sx={{
              display: { xs: 'block', sm: 'none' },
              py: { xs: 2, sm: 0 },
              fontFamily: 'InterSemiBold',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Scanning Done
          </Typography>
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontFamily: 'InterRegular',
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            Scanning Done
          </Typography>
        </Box>
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={shield}
              alt="shield"
              style={{
                width: '24px',
                height: '24px',
                display: 'block',
                margin: 'auto',
                marginRight: '10px',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'InterRegular',
                fontSize: 14,
                fontWeight: 400,
              }}
            >
              Next scan date:{' '}
              {new Date(
                new Date(lastScanStartDate).setMonth(
                  lastScanStartDate.getMonth() + 1
                )
              ).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </Box >
    );
  };

  if (!lastScanStart) {
    console.log("❌ lastScanStart missing, returning null");
    return null;
  }

  // if (scanCompletionMailSentAt && lastScanStart) {
  //   const scanCompletionMailDate = new Date(scanCompletionMailSentAt);

  //   if (lastScanStartDate < scanCompletionMailDate) {
  //     return scanDone();
  //   }
  // }

  if (lastScanCompletedAtDate == null || (lastScanStartDate > lastScanCompletedAtDate)) {
    return (
      <Box p={2} sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <img src={scanningAnimation} width="24px" alt="scanning" />
          <Typography
            sx={{
              fontFamily: 'InterRegular',
              fontSize: 14,
              fontWeight: 400,
              whiteSpace: 'nowrap',
            }}
          >
            {isMobile ? 'Scanning...' : 'Scanning 200 data broker websites...'}
          </Typography >
          {(isScanActive && totalScans > 0) && (
            <Box sx={{ flexGrow: 1, mx: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 19,
                  borderRadius: 5,
                  backgroundColor: '#E3DEF7 !important',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#7459D9',
                    borderRadius: 5,
                  },
                }}
              />
            </Box>
          )
          }
        </Box >
      </Box >
    );
  }

  return scanDone();
};
